import DoneIcon from "@mui/icons-material/DoneTwoTone";
import Filter from "@mui/icons-material/FilterAltTwoTone";
import { Autocomplete, css, TextField } from "@mui/material";
import { theme } from "../../design/theme";
import type { GetPublicationsOutput } from "../../utils/trpc";
import { useUser } from "../hooks/useUser";
import { FlexRowContainer } from "./layout";

interface PublicationFilterProps {
  publications?: GetPublicationsOutput;
  selectedPublications?: { id: number | string; title: string }[];
  setSelectedPublications: (
    publications: { id: number | string; title: string }[],
  ) => void;
  includeFRJ?: boolean;
  hideIcon?: boolean;
}

const ALL_OPTION = { id: "__ALL__", title: "All" };
const FRJ_OPTION = { id: "FRJ", title: "Financial Remedies Journal" };

export const PublicationFilter = ({
  publications = [],
  selectedPublications = [],
  setSelectedPublications,
  includeFRJ = false,
  hideIcon = false,
}: PublicationFilterProps) => {
  const { user } = useUser();

  const options = [
    ALL_OPTION,
    ...publications,
    ...(includeFRJ && user?.frjAccess ? [FRJ_OPTION] : []),
  ];

  const handleChange = (
    _: unknown,
    value: { id: number | string; title: string }[],
  ) => {
    if (value.find((option) => option.id === ALL_OPTION.id)) {
      setSelectedPublications([]);
      return;
    }
    setSelectedPublications(value);
  };

  const selectedOptions =
    selectedPublications.length === 0 ? [] : selectedPublications;

  return (
    <FlexRowContainer>
      {!hideIcon && <Filter sx={{ height: "24px" }} />}
      <div
        css={({ customTypography }) => css`
          ${customTypography.paragraph._3}
        `}
      >
        Publications:
      </div>
      <Autocomplete
        multiple
        options={options}
        getOptionLabel={(option) => option.title}
        value={selectedOptions}
        onChange={handleChange}
        disableCloseOnSelect
        size="small"
        isOptionEqualToValue={(opt, val) => opt.id === val.id}
        renderOption={(props, option, { selected }) => {
          return (
            <li {...props} key={option.id}>
              <FlexRowContainer
                style={{ justifyContent: "space-between", width: "100%" }}
              >
                <span
                  style={{
                    ...theme.customTypography.paragraph._3,
                  }}
                >
                  {option.title}
                </span>
                {(selected ||
                  (selectedPublications.length === 0 &&
                    option.id === ALL_OPTION.id)) && (
                  <DoneIcon fontSize="small" />
                )}
              </FlexRowContainer>
            </li>
          );
        }}
        renderInput={(params) => {
          const hasSelected = selectedPublications.length > 0;
          const modifiedParams = {
            ...params,
            InputProps: {
              ...params.InputProps,
              startAdornment: hasSelected ? (
                params.InputProps.startAdornment
              ) : (
                <span
                  style={{
                    marginLeft: 8,
                    ...theme.customTypography.paragraph._3,
                  }}
                >
                  All
                </span>
              ),
            },
          };

          return <TextField {...modifiedParams} size="small" />;
        }}
        renderTags={() => (
          <span style={theme.customTypography.paragraph._3}>
            {`${selectedPublications.length} selected`}
          </span>
        )}
        css={({ breakpoints }) => css`
          width: 250px;

          @media (max-width: ${breakpoints.values.xl}px) {
            width: 150px;
          }
        `}
      />
    </FlexRowContainer>
  );
};
